// src/pages/PrintHouseManagement/index.tsx
// README.md mục 7 — danh mục Nhà in (CRUD), copy khung pages/TicketTypeManagement/index.tsx.
// Khác biệt: xóa thật (modal xác nhận riêng) thay vì khóa/mở khóa; field Code chỉ hiển thị (không
// sửa được khi Update — đúng UpdatePrintHouseCommand không nhận Code).

import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { useBrandingTitle } from 'hooks';
import {
  fetchPrintHouses,
  createPrintHouse,
  updatePrintHouse,
  deletePrintHouse,
} from 'slices/printHouse/thunk';
import {
  setPage,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
  setSelectedPrintHouse,
} from 'slices/printHouse/reducer';
import { ACTIVE_STATUS_OPTIONS, CreatePrintHouseDto, PrintHouse, UpdatePrintHouseDto } from './types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('print_house_management');

const initialCreateForm: CreatePrintHouseDto = {
  code: '',
  name: '',
  address: '',
  contactName: '',
  contactPhone: '',
  note: '',
};

const PrintHouseManagementPage: React.FC = () => {
  useBrandingTitle(st('quan_ly_nha_in_text'));

  const dispatch = useDispatch();
  const location = useLocation();
  const {
    printHouses,
    selectedPrintHouse,
    pagination,
    filters,
    loading,
    submitting,
    modalState,
  } = useSelector((state: any) => state.PrintHouse);

  const [createForm, setCreateForm] = useState<CreatePrintHouseDto>(initialCreateForm);
  const [editForm, setEditForm] = useState<UpdatePrintHouseDto | null>(null);
  const [searchValue, setSearchValue] = useState(filters.searchTerm || '');
  const [deleteTarget, setDeleteTarget] = useState<PrintHouse | null>(null);

  const ACTION_COLUMN_CODES = ['button-edit', 'button-delete'];
  const canShowActionColumn = PageActionPermission.canDisplayAny(location.pathname, ACTION_COLUMN_CODES);

  const loadPrintHouses = () => {
    dispatch(fetchPrintHouses({
      pageIndex: pagination.currentPage,
      pageSize: pagination.pageSize,
      search: filters.searchTerm || undefined,
      isActive: filters.isActive === 'All' ? undefined : filters.isActive === 'true',
    }) as any);
  };

  useEffect(() => {
    loadPrintHouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  // Debounce ô tìm kiếm — chỉ dispatch vào filters (và gọi API) sau 500ms ngừng gõ
  useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timer = setTimeout(() => {
      dispatch(setFilters({ searchTerm: searchValue }));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, dispatch]);

  useEffect(() => {
    if (filters.searchTerm === '') setSearchValue('');
  }, [filters.searchTerm]);

  const handleOpenCreate = () => {
    setCreateForm(initialCreateForm);
    dispatch(openModal('create'));
  };

  const handleOpenEdit = (printHouse: PrintHouse) => {
    dispatch(setSelectedPrintHouse(printHouse));
    setEditForm({
      id: printHouse.id,
      name: printHouse.name,
      address: printHouse.address,
      contactName: printHouse.contactName || '',
      contactPhone: printHouse.contactPhone || '',
      note: printHouse.note || '',
      isActive: printHouse.isActive,
    });
    dispatch(openModal('edit'));
  };

  const handleCloseCreate = () => {
    if (submitting) return;
    dispatch(closeModal('create'));
  };

  const handleCloseEdit = () => {
    if (submitting) return;
    dispatch(closeModal('edit'));
    dispatch(setSelectedPrintHouse(null));
    setEditForm(null);
  };

  const handleCreateSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = await dispatch(createPrintHouse(createForm) as any);
    if (createPrintHouse.fulfilled.match(result)) {
      toast.success(st('tao_nha_in_thanh_cong_message'));
      loadPrintHouses();
    } else {
      toast.error((result.payload as string) || st('khong_the_tao_nha_in_message'));
    }
  };

  const handleEditSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editForm) return;
    const result = await dispatch(updatePrintHouse(editForm) as any);
    if (updatePrintHouse.fulfilled.match(result)) {
      toast.success(st('cap_nhat_nha_in_thanh_cong_message'));
      loadPrintHouses();
    } else {
      toast.error((result.payload as string) || st('khong_the_cap_nhat_nha_in_message'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await dispatch(deletePrintHouse(deleteTarget.id) as any);
    if (deletePrintHouse.fulfilled.match(result)) {
      toast.success(st('da_xoa_nha_in_message'));
      setDeleteTarget(null);
    } else {
      toast.error((result.payload as string) || st('khong_the_xoa_nha_in_message'));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('quan_ly_nha_in_text')} pageTitle={st('bao_cao_phat_hanh_text')} />

          <Card>
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('danh_muc_nha_in_title')}</h5>
                </div>
                <div className="col-sm-auto">
                  <div className="d-flex gap-1 flex-wrap">
                    {PageActionPermission.canDisplay(location.pathname, 'button-add') && (
                      <Button
                        color="success"
                        data-action-code="button-add"
                        data-action-name="Thêm nhà in"
                        onClick={handleOpenCreate}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {st('them_nha_in_button')}
                      </Button>
                    )}
                    <Button color="soft-secondary" disabled={loading} onClick={() => dispatch(clearFilters())}>
                      <i className="ri-refresh-line align-bottom me-1"></i>
                      {st('xoa_loc_button')}
                    </Button>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end">
                <Col md={4}>
                  <Label className="form-label">{st('tim_kiem_label')}</Label>
                  <Input
                    type="text"
                    placeholder={st('ma_hoac_ten_nha_in_placeholder')}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('trang_thai_label')}</Label>
                  <Input
                    type="select"
                    value={filters.isActive}
                    onChange={(event) => dispatch(setFilters({ isActive: event.target.value as any }))}
                  >
                    {ACTIVE_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Input>
                </Col>
              </Row>

              <DataTableShell
                headerRow={
                  <tr>
                    <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
                    <th>{st('ma_nha_in_column')}</th>
                    <th>{st('ten_nha_in_column')}</th>
                    <th>{st('dia_chi_column')}</th>
                    <th>{st('nguoi_lien_he_column')}</th>
                    <th>{st('sdt_column')}</th>
                    <th>{st('trang_thai_label')}</th>
                    {canShowActionColumn && <th style={{ width: 140 }}>{st('thao_tac_column')}</th>}
                  </tr>
                }
              >
                {loading ? (
                  Array.from({ length: 3 }).map((_, row) => (
                    <tr key={row}>
                      {Array.from({ length: canShowActionColumn ? 8 : 7 }).map((__, col) => (
                        <td key={col}>
                          <div className="placeholder-glow">
                            <span className="placeholder col-8"></span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : printHouses.length === 0 ? (
                  <tr>
                    <td colSpan={canShowActionColumn ? 8 : 7} className="text-center text-muted py-5">
                      <div className="mb-2"><i className="ri-building-line fs-1 text-muted"></i></div>
                      {st('chua_co_nha_in_nao_text')}
                    </td>
                  </tr>
                ) : (
                  printHouses.map((printHouse: PrintHouse, rowIndex: number) => (
                    <tr key={printHouse.id}>
                      <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, rowIndex)}</td>
                      <td className="fw-medium">{printHouse.code}</td>
                      <td>{printHouse.name}</td>
                      <td>{printHouse.address}</td>
                      <td>{printHouse.contactName || '-'}</td>
                      <td>{printHouse.contactPhone || '-'}</td>
                      <td>
                        <span className={`badge bg-${printHouse.isActive ? 'success' : 'danger'}-subtle text-${printHouse.isActive ? 'success' : 'danger'}`}>
                          {printHouse.isActive ? st('dang_hoat_dong_text') : st('ngung_hoat_dong_text')}
                        </span>
                      </td>
                      {canShowActionColumn && (
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1">
                            {PageActionPermission.canDisplay(location.pathname, 'button-edit') && (
                              <Button
                                color="secondary"
                                outline
                                size="sm"
                                className="btn-icon"
                                title={st('sua_title')}
                                data-action-code="button-edit"
                                data-action-name="Sửa nhà in"
                                disabled={submitting}
                                onClick={() => handleOpenEdit(printHouse)}
                              >
                                <i className="ri-pencil-line"></i>
                              </Button>
                            )}
                            {PageActionPermission.canDisplay(location.pathname, 'button-delete') && (
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                className="btn-icon"
                                title={st('xoa_title')}
                                data-action-code="button-delete"
                                data-action-name="Xóa nhà in"
                                disabled={submitting}
                                onClick={() => setDeleteTarget(printHouse)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </DataTableShell>

              <Row className="mt-3 align-items-center">
                <Col md={6}>
                  <span className="text-muted">
                    {st('trang_text')} {pagination.currentPage}{pagination.totalPages ? ` / ${pagination.totalPages}` : ''}
                  </span>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-end gap-2">
                    <Button size="sm" color="light" disabled={pagination.currentPage <= 1} onClick={() => dispatch(setPage(pagination.currentPage - 1))}>
                      {st('truoc_button')}
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      disabled={pagination.totalPages > 0 && pagination.currentPage >= pagination.totalPages}
                      onClick={() => dispatch(setPage(pagination.currentPage + 1))}
                    >
                      {st('sau_button')}
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={modalState.create} toggle={handleCloseCreate} centered>
        <ModalHeader className="bg-light p-3" toggle={handleCloseCreate}>{st('them_nha_in_button')}</ModalHeader>
        <Form onSubmit={handleCreateSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>{st('ma_nha_in_column')} <span className="text-danger">*</span></Label>
              <Input
                required
                placeholder={st('vd_nin01_placeholder')}
                value={createForm.code}
                data-action-code="create-input-code"
                data-action-name="Mã nhà in (Thêm mới)"
                disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-code')}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, code: event.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{st('ten_nha_in_column')} <span className="text-danger">*</span></Label>
              <Input
                required
                value={createForm.name}
                data-action-code="create-input-name"
                data-action-name="Tên nhà in (Thêm mới)"
                disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-name')}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{st('dia_chi_column')} <span className="text-danger">*</span></Label>
              <Input
                required
                value={createForm.address}
                data-action-code="create-input-address"
                data-action-name="Địa chỉ (Thêm mới)"
                disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-address')}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, address: event.target.value }))}
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{st('nguoi_lien_he_column')}</Label>
                  <Input
                    value={createForm.contactName}
                    data-action-code="create-input-contact-name"
                    data-action-name="Người liên hệ (Thêm mới)"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-contact-name')}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, contactName: event.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{st('so_dien_thoai_label')}</Label>
                  <Input
                    value={createForm.contactPhone}
                    data-action-code="create-input-contact-phone"
                    data-action-name="Số điện thoại (Thêm mới)"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-contact-phone')}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, contactPhone: event.target.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>{st('ghi_chu_label')}</Label>
              <Input
                type="textarea"
                rows={2}
                value={createForm.note}
                data-action-code="create-input-note"
                data-action-name="Ghi chú (Thêm mới)"
                disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-note')}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, note: event.target.value }))}
              />
            </FormGroup>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button color="light" onClick={handleCloseCreate} disabled={submitting}>{st('huy_button')}</Button>
              {PageActionPermission.canDisplay(location.pathname, 'button-create-submit') && (
                <Button color="success" type="submit" data-action-code="button-create-submit" data-action-name="Tạo nhà in" disabled={submitting}>
                  {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                  {st('tao_nha_in_button')}
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Modal>

      <Modal isOpen={modalState.edit} toggle={handleCloseEdit} centered>
        <ModalHeader className="bg-light p-3" toggle={handleCloseEdit}>
          {st('sua_nha_in_title')} {selectedPrintHouse?.code}
        </ModalHeader>
        <Form onSubmit={handleEditSubmit}>
          <ModalBody>
            {editForm && (
              <>
                <FormGroup>
                  <Label>{st('ten_nha_in_column')} <span className="text-danger">*</span></Label>
                  <Input
                    required
                    value={editForm.name}
                    data-action-code="edit-input-name"
                    data-action-name="Tên nhà in (Sửa)"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'edit-input-name')}
                    onChange={(event) => setEditForm((prev) => prev && ({ ...prev, name: event.target.value }))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{st('dia_chi_column')} <span className="text-danger">*</span></Label>
                  <Input
                    required
                    value={editForm.address}
                    data-action-code="edit-input-address"
                    data-action-name="Địa chỉ (Sửa)"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'edit-input-address')}
                    onChange={(event) => setEditForm((prev) => prev && ({ ...prev, address: event.target.value }))}
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('nguoi_lien_he_column')}</Label>
                      <Input
                        value={editForm.contactName}
                        data-action-code="edit-input-contact-name"
                        data-action-name="Người liên hệ (Sửa)"
                        disabled={!PageActionPermission.canEnable(location.pathname, 'edit-input-contact-name')}
                        onChange={(event) => setEditForm((prev) => prev && ({ ...prev, contactName: event.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('so_dien_thoai_label')}</Label>
                      <Input
                        value={editForm.contactPhone}
                        data-action-code="edit-input-contact-phone"
                        data-action-name="Số điện thoại (Sửa)"
                        disabled={!PageActionPermission.canEnable(location.pathname, 'edit-input-contact-phone')}
                        onChange={(event) => setEditForm((prev) => prev && ({ ...prev, contactPhone: event.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label>{st('ghi_chu_label')}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={editForm.note}
                    data-action-code="edit-input-note"
                    data-action-name="Ghi chú (Sửa)"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'edit-input-note')}
                    onChange={(event) => setEditForm((prev) => prev && ({ ...prev, note: event.target.value }))}
                  />
                </FormGroup>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="editIsActive"
                    checked={editForm.isActive}
                    data-action-code="edit-input-is-active"
                    data-action-name="Trạng thái hoạt động (Sửa)"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'edit-input-is-active')}
                    onChange={(event) => setEditForm((prev) => prev && ({ ...prev, isActive: event.target.checked }))}
                  />
                  <Label check htmlFor="editIsActive">{st('dang_hoat_dong_text')}</Label>
                </FormGroup>
              </>
            )}
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button color="light" onClick={handleCloseEdit} disabled={submitting}>{st('huy_button')}</Button>
              {PageActionPermission.canDisplay(location.pathname, 'button-edit-submit') && (
                <Button color="success" type="submit" data-action-code="button-edit-submit" data-action-name="Lưu thay đổi nhà in" disabled={submitting}>
                  {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                  {st('luu_thay_doi_button')}
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Modal>

      <Modal isOpen={!!deleteTarget} toggle={() => !submitting && setDeleteTarget(null)} centered>
        <ModalHeader className="bg-light p-3" toggle={() => !submitting && setDeleteTarget(null)}>{st('xac_nhan_xoa_title')}</ModalHeader>
        <ModalBody>
          {st('ban_co_chac_chan_muon_xoa_text')} <strong>{deleteTarget?.name}</strong> ({deleteTarget?.code}{st('khong_text')}
        </ModalBody>
        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <Button color="light" onClick={() => setDeleteTarget(null)} disabled={submitting}>{st('huy_button')}</Button>
            <Button color="danger" onClick={handleConfirmDelete} disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {st('xoa_title')}
            </Button>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default PrintHouseManagementPage;
